import { useDispatch, useSelector } from 'react-redux';
import { analytics } from 'external-libs/segment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  canUseCallTracking,
  canUseVoip,
  fetchOrganizationFeatures,
} from 'resources/Organization/Features/FeaturesData';
import { showGetStartedChecklist } from 'resources/Pro/ProData';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';

import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';
import { useDataFetcherV2 } from 'components/Common/Data/useDataFetcher';
import { useHomepageMVP } from 'components/DashboardV2/displayConfig';
import { useMyMoneyNotificationsCount } from 'components/MyMoneyV2/context/MyMoneyNotificationsCountContext';
import usePricebookBadgeValue from 'components/Settings/SettingsV2/hooks/usePricebookBadgeValue';

import { CUSTOMER_CHAT_WEB, EMPLOYEE_CHAT_WEB } from '../../LaunchDarkly/FeatureKeys';
import { NAVBAR_MENU, trackButtonClicked } from '../../Services/Analytics/TrackEventsService';

import { useMessageCount } from './MessagesCountHook';
import NavigationSplashLabel from './NavigationSplashLabel';

const TabState = ({
  getStartedDisplaySettings,
  canUsePipeline,
  canUsePriceBook,
  showReporting,
  showCompanySetup,
  pricebookBadgeData,
}) => {
  useDataFetcherV2(fetchOrganizationFeatures);
  const launchDarklyFlags = useFlags();
  const messagesCount = useMessageCount();
  const dispatch = useDispatch();
  const hasVoipFeature = useSelector(canUseVoip);
  const hasCallTrackingFeature = useSelector(canUseCallTracking);
  const showInboxFlag = !!(
    launchDarklyFlags[EMPLOYEE_CHAT_WEB] ||
    launchDarklyFlags[CUSTOMER_CHAT_WEB] ||
    hasCallTrackingFeature ||
    hasVoipFeature
  );
  const showGetStarted = useSelector(showGetStartedChecklist);

  const { badgeValue: pricebookBadgeValue } = usePricebookBadgeValue({ pricebookBadgeData });

  const isConquerEnabled = useIsExperimentActive(AmplitudeFlagKeys.conquer);
  const { homepageBetaFeatureActive } = useHomepageMVP();
  const myMoneyNotificationsCount = useMyMoneyNotificationsCount();
  const getStartedEngagement = useIsExperimentActive(AmplitudeFlagKeys.getStartedEngagement);
  const canUseMarketing = useIsExperimentActive(AmplitudeFlagKeys.marketing);

  const { CONQUER_URL } = window;

  return [
    {
      badgeCount: getStartedEngagement ? null : getStartedDisplaySettings?.tasks_remaining_count,
      href: '/app/get_started',
      label: 'Get started',
      showTab: getStartedDisplaySettings?.show_get_started && showGetStarted,
      value: 'get_started',
    },
    {
      href: '/pro/dashboard',
      label: homepageBetaFeatureActive ? 'Home' : 'Dash',
      showTab: showReporting,
      value: 'dashboard',
    },
    {
      badgeCount: messagesCount,
      href: '/pro/messages',
      label: NavigationSplashLabel('Inbox'),
      showTab: showInboxFlag,
      value: 'messages',
    },
    {
      href: '/pro/calendar',
      label: 'Schedule',
      showTab: true,
      value: 'calendar',
    },
    {
      href: '/pro/customers',
      label: 'Customers',
      showTab: true,
      value: 'customers',
    },
    {
      href: '/app/pipeline',
      label: NavigationSplashLabel('Pipeline'),
      showTab: canUsePipeline,
      value: 'pipeline',
    },
    {
      badgeCount: myMoneyNotificationsCount,
      href: '/pro/my_money',
      label: 'My money',
      showTab: showCompanySetup,
      value: 'money',
    },
    {
      href: '/app/reporting',
      onClick: () => {
        dispatch(trackButtonClicked('Reporting', { button_location: NAVBAR_MENU }));
      },
      label: NavigationSplashLabel('Reporting'),
      showTab: showReporting,
      value: 'reporting',
    },
    {
      href: '/app/marketing',
      label: NavigationSplashLabel('Marketing'),
      showTab: canUseMarketing,
      value: 'marketing',
    },
    {
      badgeCount: pricebookBadgeValue,
      href: '/app/price_book',
      label: 'Price book',
      showTab: canUsePriceBook,
      value: 'price_book',
    },
    {
      href: CONQUER_URL,
      onClick: () => {
        analytics.track('Conquer tab clicked', {
          button_label: 'conquer',
          button_name: 'launch conquer',
        });
      },
      label: 'CONQUER',
      showTab: isConquerEnabled,
      value: 'conquer',
    },
  ];
};

export default TabState;
